import moment from 'moment-timezone';
import _ from 'lodash';

/**
 * Returns Time formatted as hh:mm in the specified time zone
 * For Mobile View
 * @param {number} epoch - Time in epoch
 * @param {string} timeZone - The timezone
 */
export const convertToTimeZonehhmm = (epoch, timeZone) => {
  if (epoch === 0) {
    return '-';
  }
  return `${moment.tz(epoch, timeZone).format('HH:mm')} Hr`;
};

/**
 * Format for sitepoints
 * @param {number} epoch - Time in epoch
 * @param {string} timeZone - The timezone
 */
export const convertForSite = (epoch, timeZone) => {
  if (epoch === 0) {
    return '-';
  }
  return `${moment.tz(epoch, timeZone).format('HH:mm')} Hr`;
};

/**
 * Returns date for api requests
 */
export const getDateForCaching = () => {
  const currentDate = new Date();
  const formattedDate = moment(currentDate).format('DD-MM-YYYY');
  return `${formattedDate}/${Math.floor(currentDate.getHours() / 2)}`;
};

/**
 * Returns time as follows - 17:15 Hr, 14 Apr, 2020
 * @param {number} epoch - Time in epoch
 * @param {string} timeZone - The timezone
 */
export const convertToTimeZoneHHMMDDMMMYYYY = (epoch, timeZone) => {
  const momentTime = timeZone ? moment.tz(epoch, timeZone) : moment(epoch);
  const time = momentTime.format('HH:mm');
  const date = momentTime.format('DD MMM, YYYY');
  const zoneAbbr = momentTime.format('z');
  const liveConnect = epoch <= 0 ? '-' : `${date}, ${zoneAbbr}, ${time} Hrs`;

  return {
    time,
    date,
    formatted: epoch <= 0 ? '-' : `${time} Hr, ${date}`,
    liveConnect,
  };
};

export const getLatestTime = rowTimeArr => {
  const maxTime = _.max(rowTimeArr);
  return maxTime;
};

export const getOldestTime = rowTimeArr => {
  const minTime = _.min(rowTimeArr);
  return minTime;
};

/**
 * Returns difference between 2 dates
 * @param {string} startDate - Date in YYYY-MM-DD format
 * @param {string} endDate - Date in YYYY-MM-DD format
 */
export const getDifferenceBetweenDates = (startDate, endDate) =>
  Math.round(
    (Date.parse(endDate) - Date.parse(startDate)) / (1000 * 60 * 60 * 24),
  );

export const daysFromNow = date =>
  Math.round((Date.now() - Date.parse(date)) / (1000 * 60 * 60 * 24));

/**
 * Returns difference between 2 dates
 * @param {object} date - Date obj
 * @param {string} delimeter - Delimeter to separate dates
 */
export const formatDateToYYYYMMDD = (date, delimeter) => {
  const d = new Date(date);
  let month = (d.getMonth() + 1).toString();
  let day = d.getDate().toString();
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join(delimeter);
};

/** Sanitize a report name for use as the browser print / Save-as-PDF filename. */
export const sanitizePrintFileName = name => {
  if (!name || typeof name !== 'string') {
    return 'report';
  }
  const sanitized = name.replace(/[\\/:*?"<>|]/g, '-').trim();
  return sanitized || 'report';
};

export const monthFormatter = (locale = 'en') =>
  new Intl.DateTimeFormat(locale, { month: 'short' });

export const getDateWithoutTime = date =>
  moment(moment(date).format('MM/DD/YYYY')).toDate().getTime();

export const convertToLastLogonTime = epoch => {
  if (!epoch) {
    return '-';
  }
  return moment.utc(moment.unix(epoch)).format('HH:mm, DD MMM, YYYY, z');
};

/**
 * @param {ms} epoch
 * @param {ms} threshold
 * @returns boolean
 */
export const isRecentTime = (epoch, threshold) => {
  const isRecent = Date.now() - epoch <= threshold;
  return isRecent;
};

const traslationFormats = {
  en: {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  es: {
    sameDay: '[Hoy]',
    nextDay: '[mañana]',
    nextWeek: 'dddd',
    lastDay: '[Ayer]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  ar: {
    sameDay: '[اليوم]',
    nextDay: '[غداً]',
    nextWeek: 'dddd',
    lastDay: '[أمس]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  de: {
    sameDay: '[Heute]',
    nextDay: '[Morgen]',
    nextWeek: 'dddd',
    lastDay: '[Gestern]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  fr: {
    sameDay: "[Aujourd'hui]",
    nextDay: '[Demain]',
    nextWeek: 'dddd',
    lastDay: '[Hier]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  it: {
    sameDay: '[Oggi]',
    nextDay: '[Domani]',
    nextWeek: 'dddd',
    lastDay: '[Ieri]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  nl: {
    sameDay: '[Vandaag]',
    nextDay: '[Morgen]',
    nextWeek: 'dddd',
    lastDay: '[Gisteren]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  tr: {
    sameDay: '[Bugün]',
    nextDay: '[Yarın]',
    nextWeek: 'dddd',
    lastDay: '[Dün]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  'zh-cn': {
    sameDay: '[今天]',
    nextDay: '[明天]',
    nextWeek: 'dddd',
    lastDay: '[昨天]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  zh: {
    sameDay: '[今天]',
    nextDay: '[明天]',
    nextWeek: 'dddd',
    lastDay: '[昨天]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  ja: {
    sameDay: '同じ日',
    nextDay: '次の日',
    nextWeek: 'dddd',
    lastDay: '最終日',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
  th: {
    sameDay: '[วันนี้]',
    nextDay: '[พรุ่งนี้]',
    nextWeek: 'dddd',
    lastDay: '[เมื่อวาน]',
    lastWeek: 'Do MMMM',
    sameElse: 'DD/MM/YYYY',
  },
};

moment.updateLocale('es', {
  calendar: traslationFormats.es,
});
moment.updateLocale('zh', {
  calendar: traslationFormats.zh,
});
moment.updateLocale('zh-cn', {
  calendar: traslationFormats['zh-cn'],
});
moment.updateLocale('en', {
  calendar: traslationFormats.en,
});
moment.updateLocale('ar', {
  calendar: traslationFormats.ar,
});
moment.updateLocale('de', {
  calendar: traslationFormats.de,
});
moment.updateLocale('fr', {
  calendar: traslationFormats.fr,
});
moment.updateLocale('it', {
  calendar: traslationFormats.it,
});
moment.updateLocale('nl', {
  calendar: traslationFormats.nl,
});
moment.updateLocale('ja', {
  calendar: traslationFormats.ja,
});
moment.updateLocale('tr', {
  calendar: traslationFormats.tr,
});
moment.updateLocale('th', {
  calendar: traslationFormats.th,
});

export const relativeDate = (time, locale) => {
  const now = moment();
  return moment(time).locale(locale).calendar(now, traslationFormats[locale]);
};

export const monthYearFormatter = (locale = 'en') =>
  new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
  });

export const dayFormatter = (locale = 'en') =>
  new Intl.DateTimeFormat(locale, { day: 'numeric' });

export const getCurrentTimestamp = () => new Date().getTime();

export const convertToDtMnthYrUTC = epochMS => {
  if (!epochMS) {
    return '-';
  }
  return moment.utc(moment(epochMS).valueOf()).format('DD MMM, YYYY, z');
};

export const convertToDtMnthYr = (epochMS, timezone) => {
  if (!epochMS) {
    return '-';
  }
  return !timezone
    ? convertToDtMnthYrUTC(epochMS)
    : moment.tz(epochMS, timezone).format('DD MMM, YYYY, z');
};
